import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { Document, Page, pdfjs } from 'react-pdf';
import { IconType } from 'react-icons';
import { MdOutlineNearMe, MdOutlineEdit, MdOutlineTextFields, MdUndo, MdDeleteOutline } from 'react-icons/md';
import { FaHighlighter, FaEraser } from 'react-icons/fa';
import { carregarAnotacoes, salvarAnotacoes } from './armazenamento';
import { Anotacoes, Marca, Ponto, Texto, Traco } from './tipos';

export type { Marca, Ponto, Texto, Traco } from './tipos';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type Ferramenta = 'cursor' | 'caneta' | 'marcador' | 'texto' | 'borracha';

/** Texto em edicao. Guarda a pagina de origem para nao cair na pagina errada se ela virar antes do commit. */
type Edicao = {
	id: number;
	chave: string;
	posicao: Ponto;
	conteudo: string;
	/** texto ja existente sendo reescrito, substituido no commit */
	substituindo?: Texto;
};

/** Texto sendo arrastado. `marca` acompanha a copia mais recente dele dentro das anotacoes. */
type Movimento = {
	marca: Texto;
	/** distancia entre o ponto agarrado e o canto do texto, para ele nao pular para o cursor */
	deslocamento: Ponto;
	inicio: Ponto;
	moveu: boolean;
};

/** Texto sendo redimensionado pela alca. As medidas iniciais viram a regra de tres do novo tamanho. */
type Redimensionamento = {
	marca: Texto;
	origem: Ponto;
	larguraInicial: number;
	tamanhoInicial: number;
};

type Tamanho = { largura: number; altura: number };

type PdfBoardProps = {
	file?: string;
	pageNumber: number;
	onDocumentLoadSuccess: (info: { numPages: number }) => void;
	/** identifica o documento aberto para que os rabiscos sigam a lesson, e nao a posicao na lista */
	documentKey?: string;
};

/** `dica` so muda o texto do tooltip: o nome acessivel continua sendo o rotulo. */
const FERRAMENTAS: { id: Ferramenta; rotulo: string; dica?: string; Icone: IconType }[] = [
	{ id: 'cursor', rotulo: 'Navegar', Icone: MdOutlineNearMe },
	{ id: 'caneta', rotulo: 'Caneta', Icone: MdOutlineEdit },
	{ id: 'marcador', rotulo: 'Marca-texto', Icone: FaHighlighter },
	{ id: 'texto', rotulo: 'Texto', dica: 'Texto: clique para escrever, arraste para mover', Icone: MdOutlineTextFields },
	{ id: 'borracha', rotulo: 'Borracha', Icone: FaEraser }
];

const CORES = [
	{ nome: 'Vermelho', valor: '#E53E3E' },
	{ nome: 'Azul', valor: '#1E56C8' },
	{ nome: 'Verde', valor: '#2F9E44' },
	{ nome: 'Amarelo', valor: '#F2B705' },
	{ nome: 'Preto', valor: '#0E1F38' }
];
/** Tarjas atras do texto. Sem `valor` e o fundo transparente, com que todo texto nasce. */
const FUNDOS: { nome: string; valor?: string }[] = [
	{ nome: 'Sem fundo' },
	{ nome: 'Fundo branco', valor: '#FFFFFF' },
	{ nome: 'Fundo amarelo', valor: '#FFF3BF' },
	{ nome: 'Fundo azul', valor: '#DCE8FF' },
	{ nome: 'Fundo verde', valor: '#D8F5DE' }
];
/** Risco diagonal do botao de fundo transparente, ja que ele nao tem cor para mostrar. */
const RISCO_SEM_FUNDO =
	'linear-gradient(to top right, transparent calc(50% - 1px), #E53E3E calc(50% - 1px), #E53E3E calc(50% + 1px), transparent calc(50% + 1px))';
const ESPESSURAS = [ 0.0025, 0.005, 0.009 ];
const TAMANHOS_TEXTO = [ 0.016, 0.024, 0.036 ];
const MULTIPLICADOR_MARCADOR = 4;
const ALPHA_MARCADOR = 0.32;
const RAIO_BORRACHA = 0.014;
/** Arrasto menor que isto, em px, conta como clique: mirar um texto para corrigir treme a mao. */
const LIMIAR_ARRASTO = 4;
/** Limites do redimensionamento pela alca, como fracao da largura da pagina. */
const MENOR_TAMANHO_TEXTO = 0.008;
const MAIOR_TAMANHO_TEXTO = 0.12;
const LADO_ALCA = 12;
/** Quanto cada seta do teclado cresce ou encolhe o texto escolhido. */
const PASSO_ALCA = 1.1;

const tamanhoValido = (valor: number) => Math.min(MAIOR_TAMANHO_TEXTO, Math.max(MENOR_TAMANHO_TEXTO, valor));
const ALTURA_LINHA = 1.3;
/** Respiro lateral da tarja, como fracao da altura da fonte, para as letras nao encostarem na borda. */
const FOLGA_FUNDO = 0.2;
const FONTE_TEXTO = 'Helvetica, Arial, sans-serif';
const VAZIO: Marca[] = [];

const cursorPorFerramenta: Record<Ferramenta, string> = {
	cursor: 'default',
	caneta: 'crosshair',
	marcador: 'crosshair',
	texto: 'text',
	borracha: 'cell'
};

/**
 * Observa o tamanho renderizado de um elemento e devolve o ref de medicao junto.
 * O ref e de callback porque a pagina so entra na arvore quando uma lesson e aberta,
 * e um RefObject deixaria o observer preso ao valor nulo do primeiro render.
 * Sem ResizeObserver (jsdom) o tamanho fica zerado e nada e desenhado.
 */
function useTamanho() {
	const [ elemento, setElemento ] = useState<HTMLDivElement | null>(null);
	const [ tamanho, setTamanho ] = useState<Tamanho>({ largura: 0, altura: 0 });

	useEffect(
		() => {
			if (!elemento || typeof ResizeObserver === 'undefined') return;

			const medir = () => setTamanho({ largura: elemento.clientWidth, altura: elemento.clientHeight });
			const observer = new ResizeObserver(medir);
			observer.observe(elemento);
			medir();

			return () => observer.disconnect();
		},
		[ elemento ]
	);

	return [ setElemento, tamanho ] as const;
}

/** Ajusta o buffer do canvas ao tamanho em CSS px e devolve o contexto ja escalado. */
function prepararContexto(canvas: HTMLCanvasElement | null, tamanho: Tamanho) {
	if (!canvas || !tamanho.largura || !tamanho.altura) return null;

	const dpr = window.devicePixelRatio || 1;
	canvas.width = Math.round(tamanho.largura * dpr);
	canvas.height = Math.round(tamanho.altura * dpr);

	const ctx = canvas.getContext('2d');
	if (!ctx) return null;

	ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	ctx.clearRect(0, 0, tamanho.largura, tamanho.altura);

	return ctx;
}

function desenharTraco(ctx: CanvasRenderingContext2D, traco: Traco, tamanho: Tamanho) {
	if (traco.pontos.length === 0) return;

	const pontos = traco.pontos.map((ponto) => ({
		x: ponto.x * tamanho.largura,
		y: ponto.y * tamanho.altura
	}));
	const espessura = Math.max(1, traco.espessura * tamanho.largura);

	ctx.save();
	ctx.lineCap = 'round';
	ctx.lineJoin = 'round';
	ctx.globalAlpha = traco.ferramenta === 'marcador' ? ALPHA_MARCADOR : 1;

	if (pontos.length === 1) {
		ctx.fillStyle = traco.cor;
		ctx.beginPath();
		ctx.arc(pontos[0].x, pontos[0].y, espessura / 2, 0, Math.PI * 2);
		ctx.fill();
	} else {
		ctx.strokeStyle = traco.cor;
		ctx.lineWidth = espessura;
		ctx.beginPath();
		ctx.moveTo(pontos[0].x, pontos[0].y);
		// suaviza os cantos passando a curva pelo ponto medio de cada par
		for (let i = 1; i < pontos.length - 1; i++) {
			const meio = {
				x: (pontos[i].x + pontos[i + 1].x) / 2,
				y: (pontos[i].y + pontos[i + 1].y) / 2
			};
			ctx.quadraticCurveTo(pontos[i].x, pontos[i].y, meio.x, meio.y);
		}
		ctx.lineTo(pontos[pontos.length - 1].x, pontos[pontos.length - 1].y);
		ctx.stroke();
	}

	ctx.restore();
}

/** O campo cresce junto com o conteudo, para nao aparecer barra de rolagem sobre a pagina. */
function ajustarAltura(elemento: HTMLTextAreaElement | null) {
	if (!elemento) return;

	elemento.style.height = 'auto';
	elemento.style.height = `${elemento.scrollHeight}px`;
}

/** Altura da fonte em px, com um piso para o texto nao sumir em telas pequenas. */
function fonteEmPx(tamanhoFonte: number, tamanho: Tamanho) {
	return Math.max(9, tamanhoFonte * tamanho.largura);
}

function fonteCss(tamanhoFonte: number, tamanho: Tamanho) {
	return `${fonteEmPx(tamanhoFonte, tamanho)}px ${FONTE_TEXTO}`;
}

/** Quanto a tarja avanca de cada lado do texto. O campo de edicao usa a mesma folga, como padding. */
function folgaFundo(fonteEmPixels: number) {
	return fonteEmPixels * FOLGA_FUNDO;
}

/** Linhas e caixa ocupada pelo texto, em px. Precisa do contexto porque so ele mede a fonte. */
function medirTexto(ctx: CanvasRenderingContext2D, texto: Texto, tamanho: Tamanho) {
	const linhas = texto.conteudo.split('\n');
	const alturaLinha = fonteEmPx(texto.tamanho, tamanho) * ALTURA_LINHA;

	ctx.save();
	ctx.font = fonteCss(texto.tamanho, tamanho);
	const largura = linhas.reduce((maior, linha) => Math.max(maior, ctx.measureText(linha).width), 0);
	ctx.restore();

	return { linhas, alturaLinha, largura, altura: alturaLinha * linhas.length };
}

function desenharTexto(ctx: CanvasRenderingContext2D, texto: Texto, tamanho: Tamanho) {
	const { linhas, alturaLinha, largura, altura } = medirTexto(ctx, texto, tamanho);
	const x = texto.posicao.x * tamanho.largura;
	const y = texto.posicao.y * tamanho.altura;
	const fonte = fonteEmPx(texto.tamanho, tamanho);
	// o campo de edicao usa o mesmo line-height e centra a linha nele, entao o texto
	// nao pula de lugar quando e confirmado
	const respiro = (alturaLinha - fonte) / 2;

	ctx.save();
	ctx.font = fonteCss(texto.tamanho, tamanho);
	ctx.textBaseline = 'top';

	// a tarja cobre a caixa de linha inteira, que ja traz o meio-respiro do line-height
	if (texto.fundo) {
		const folga = folgaFundo(fonte);
		ctx.fillStyle = texto.fundo;
		ctx.fillRect(x - folga, y, largura + folga * 2, altura);
	}

	ctx.fillStyle = texto.cor;
	linhas.forEach((linha, indice) => ctx.fillText(linha, x, y + respiro + indice * alturaLinha));
	ctx.restore();
}

function desenharMarca(ctx: CanvasRenderingContext2D, marca: Marca, tamanho: Tamanho) {
	if (marca.ferramenta === 'texto') {
		desenharTexto(ctx, marca, tamanho);

		return;
	}

	desenharTraco(ctx, marca, tamanho);
}

/** Distancia em px entre o ponto e o segmento, usada pela borracha. */
function distanciaAteSegmento(ponto: Ponto, inicio: Ponto, fim: Ponto, tamanho: Tamanho) {
	const px = (valor: number) => valor * tamanho.largura;
	const py = (valor: number) => valor * tamanho.altura;

	const ax = px(inicio.x);
	const ay = py(inicio.y);
	const bx = px(fim.x);
	const by = py(fim.y);
	const cx = px(ponto.x);
	const cy = py(ponto.y);

	const comprimento = (bx - ax) ** 2 + (by - ay) ** 2;
	const t = comprimento === 0 ? 0 : Math.max(0, Math.min(1, ((cx - ax) * (bx - ax) + (cy - ay) * (by - ay)) / comprimento));

	return Math.hypot(cx - (ax + t * (bx - ax)), cy - (ay + t * (by - ay)));
}

function tracoAtingido(traco: Traco, ponto: Ponto, tamanho: Tamanho) {
	const raio = RAIO_BORRACHA * tamanho.largura + traco.espessura * tamanho.largura / 2;

	if (traco.pontos.length === 1) {
		return distanciaAteSegmento(ponto, traco.pontos[0], traco.pontos[0], tamanho) <= raio;
	}

	for (let i = 0; i < traco.pontos.length - 1; i++) {
		if (distanciaAteSegmento(ponto, traco.pontos[i], traco.pontos[i + 1], tamanho) <= raio) return true;
	}

	return false;
}

/** O texto e pego pela caixa que ocupa, e nao pelo desenho das letras: mirar num buraco de letra seria cruel. */
function textoAtingido(ctx: CanvasRenderingContext2D | null, texto: Texto, ponto: Ponto, tamanho: Tamanho) {
	if (!ctx) return false;

	const { largura, altura } = medirTexto(ctx, texto, tamanho);
	const folga = fonteEmPx(texto.tamanho, tamanho) / 4;
	const x = ponto.x * tamanho.largura;
	const y = ponto.y * tamanho.altura;
	const esquerda = texto.posicao.x * tamanho.largura;
	const topo = texto.posicao.y * tamanho.altura;

	return (
		x >= esquerda - folga && x <= esquerda + largura + folga && y >= topo - folga && y <= topo + altura + folga
	);
}

function marcaAtingida(ctx: CanvasRenderingContext2D | null, marca: Marca, ponto: Ponto, tamanho: Tamanho) {
	return marca.ferramenta === 'texto' ? textoAtingido(ctx, marca, ponto, tamanho) : tracoAtingido(marca, ponto, tamanho);
}

/**
 * Grava o texto digitado nas anotacoes da pagina de onde ele saiu.
 * Reescrever um texto existente troca o original de lugar na lista, entao apagar
 * um texto e apagar o conteudo dele dao no mesmo resultado.
 */
function aplicarEdicao(
	anotacoes: Anotacoes,
	edicao: Edicao,
	estilo: { cor: string; tamanho: number; fundo?: string }
): Anotacoes {
	const conteudo = edicao.conteudo.trim();
	const daPagina = anotacoes[edicao.chave] || VAZIO;
	const semOriginal = edicao.substituindo ? daPagina.filter((marca) => marca !== edicao.substituindo) : daPagina;

	const atualizadas: Marca[] = conteudo
		? semOriginal.concat({
				ferramenta: 'texto',
				cor: estilo.cor,
				tamanho: estilo.tamanho,
				fundo: estilo.fundo,
				posicao: edicao.posicao,
				conteudo
			})
		: semOriginal;

	return { ...anotacoes, [edicao.chave]: atualizadas };
}

export default function PdfBoard({ file, pageNumber, onDocumentLoadSuccess, documentKey }: PdfBoardProps) {
	const canvasBaseRef = useRef<HTMLCanvasElement>(null);
	const canvasAtualRef = useRef<HTMLCanvasElement>(null);
	const tracoAtualRef = useRef<Traco | null>(null);
	const apagandoRef = useRef(false);
	const anotacoesRef = useRef<Anotacoes>({});
	const edicaoRef = useRef<Edicao | null>(null);
	const movimentoRef = useRef<Movimento | null>(null);
	const redimensionamentoRef = useRef<Redimensionamento | null>(null);
	const campoRef = useRef<HTMLTextAreaElement | null>(null);
	const estiloTextoRef = useRef({ cor: CORES[0].valor, tamanho: TAMANHOS_TEXTO[1], fundo: FUNDOS[0].valor });
	// Tamanho com que um texto novo nasce. So a paleta mexe nele: redimensionar pela alca ou
	// escolher um texto ja escrito muda o tamanho corrente, e sem esta memoria o proximo texto
	// nasceria do tamanho do ultimo arrasto, que pode nem estar em nenhum botao da paleta.
	const tamanhoNovoTextoRef = useRef(TAMANHOS_TEXTO[1]);
	const proximaEdicaoRef = useRef(0);

	const [ ferramenta, setFerramenta ] = useState<Ferramenta>('cursor');
	const [ cor, setCor ] = useState(CORES[0].valor);
	const [ espessura, setEspessura ] = useState(ESPESSURAS[1]);
	const [ tamanhoTexto, setTamanhoTexto ] = useState(TAMANHOS_TEXTO[1]);
	const [ fundo, setFundo ] = useState(FUNDOS[0].valor);
	const [ proporcao, setProporcao ] = useState(0);
	const [ anotacoes, setAnotacoes ] = useState<Anotacoes>(carregarAnotacoes);
	const [ edicao, setEdicao ] = useState<Edicao | null>(null);
	const [ sobreTexto, setSobreTexto ] = useState(false);
	const [ selecionado, setSelecionado ] = useState<Texto | null>(null);

	const [ medirArea, area ] = useTamanho();
	const [ medirPagina, tela ] = useTamanho();

	const chave = `${documentKey || 'documento'}#${pageNumber}`;
	const marcas = anotacoes[chave] || VAZIO;

	// Persiste com um respiro: apagar arrastando a borracha muda o estado varias vezes por segundo.
	useEffect(
		() => {
			anotacoesRef.current = anotacoes;
			const id = setTimeout(() => salvarAnotacoes(anotacoes), 400);

			return () => clearTimeout(id);
		},
		[ anotacoes ]
	);

	useEffect(
		() => {
			estiloTextoRef.current = { cor, tamanho: tamanhoTexto, fundo };
		},
		[ cor, tamanhoTexto, fundo ]
	);

	// A janela do material se fecha sozinha quando o tempo da aula acaba, entao o ultimo
	// rabisco nao pode depender do timer acima ter disparado. O texto ainda no cursor
	// entra junto, senao fechar a janela no meio de uma frase a jogaria fora.
	useEffect(() => {
		const salvar = () => {
			const pendente = edicaoRef.current;

			salvarAnotacoes(
				pendente && pendente.conteudo.trim()
					? aplicarEdicao(anotacoesRef.current, pendente, estiloTextoRef.current)
					: anotacoesRef.current
			);
		};
		window.addEventListener('pagehide', salvar);

		return () => {
			window.removeEventListener('pagehide', salvar);
			salvar();
		};
	}, []);

	// Encaixa a pagina na area disponivel: cabendo pela altura usamos a altura,
	// senao ocupamos a largura toda. Antes de conhecer a proporcao, largura cheia.
	const larguraPagina = useMemo(
		() => {
			if (!area.largura) return 0;
			if (!proporcao) return area.largura;

			return Math.floor(Math.min(area.largura, area.altura * proporcao));
		},
		[ area.largura, area.altura, proporcao ]
	);

	// O texto reaberto sai do canvas enquanto e corrigido: como o campo fica por cima dele,
	// trocar o tamanho da fonte deixaria o desenho antigo aparecendo por baixo do novo.
	const textoEmEdicao = edicao?.substituindo;

	useEffect(
		() => {
			const ctx = prepararContexto(canvasBaseRef.current, tela);
			if (!ctx) return;

			marcas.forEach((marca) => marca !== textoEmEdicao && desenharMarca(ctx, marca, tela));
		},
		[ marcas, tela, textoEmEdicao ]
	);

	useEffect(
		() => {
			prepararContexto(canvasAtualRef.current, tela);
		},
		[ tela, chave ]
	);

	const desenharTracoAtual = useCallback(
		() => {
			const traco = tracoAtualRef.current;
			const ctx = canvasAtualRef.current?.getContext('2d');
			if (!ctx) return;

			ctx.clearRect(0, 0, tela.largura, tela.altura);
			if (traco) desenharTraco(ctx, traco, tela);
		},
		[ tela ]
	);

	// O campo e remontado a cada edicao (key), entao o callback estavel roda uma vez por texto.
	const prepararCampo = useCallback((elemento: HTMLTextAreaElement | null) => {
		campoRef.current = elemento;
		if (!elemento) return;

		elemento.focus();
		elemento.setSelectionRange(elemento.value.length, elemento.value.length);
		ajustarAltura(elemento);
	}, []);

	/**
	 * O campo acompanha o que ja foi digitado em vez de tomar toda a faixa ate a margem
	 * da pagina: um campo vazio do tamanho de uma linha inteira esconde o material atras
	 * de um retangulo branco antes mesmo da primeira letra.
	 */
	const larguraCampo = (() => {
		if (!edicao || !tela.largura) return undefined;

		const fonte = fonteEmPx(tamanhoTexto, tela);
		const disponivel = (1 - edicao.posicao.x) * tela.largura;
		const minimo = Math.min(disponivel, fonte);
		const ctx = canvasBaseRef.current?.getContext('2d');
		if (!ctx) return disponivel;

		ctx.save();
		ctx.font = fonteCss(tamanhoTexto, tela);
		const conteudo = edicao.conteudo
			.split('\n')
			.reduce((maior, linha) => Math.max(maior, ctx.measureText(linha).width), 0);
		ctx.restore();

		// a folga guarda o lugar do cursor, para ele nao encostar na borda ao fim da linha
		return Math.min(disponivel, Math.max(minimo, conteudo + fonte * 0.3));
	})();

	// A tarja entra no campo como padding lateral, e a margem negativa devolve o deslocamento:
	// as letras ficam no mesmo x antes e depois de confirmar, com ou sem fundo.
	const folgaCampo = folgaFundo(fonteEmPx(tamanhoTexto, tela));

	// A altura depende de onde o texto quebra, entao so pode ser medida depois que a
	// largura acima ja esta valendo no elemento.
	useEffect(
		() => {
			ajustarAltura(campoRef.current);
		},
		[ tamanhoTexto, tela, larguraCampo, edicao?.conteudo ]
	);

	const posicaoNormalizada = (evento: React.PointerEvent<HTMLCanvasElement>): Ponto => {
		const retangulo = evento.currentTarget.getBoundingClientRect();

		return {
			x: (evento.clientX - retangulo.left) / retangulo.width,
			y: (evento.clientY - retangulo.top) / retangulo.height
		};
	};

	/** Mesma conta para quem nao e o canvas, como a alca, que se orienta por ele e nao por si. */
	const pontoNaPagina = (clientX: number, clientY: number): Ponto | null => {
		const retangulo = canvasBaseRef.current?.getBoundingClientRect();
		if (!retangulo || !retangulo.width || !retangulo.height) return null;

		return { x: (clientX - retangulo.left) / retangulo.width, y: (clientY - retangulo.top) / retangulo.height };
	};

	const apagarEm = useCallback(
		(ponto: Ponto) => {
			const ctx = canvasBaseRef.current?.getContext('2d') || null;

			setAnotacoes((anterior) => {
				const atuais = anterior[chave] || VAZIO;
				const restantes = atuais.filter((marca) => !marcaAtingida(ctx, marca, ponto, tela));
				if (restantes.length === atuais.length) return anterior;

				return { ...anterior, [chave]: restantes };
			});
		},
		[ chave, tela ]
	);

	const confirmarEdicao = useCallback(() => {
		const pendente = edicaoRef.current;
		edicaoRef.current = null;
		setEdicao(null);

		if (!pendente || (!pendente.conteudo.trim() && !pendente.substituindo)) return;

		setAnotacoes((anterior) => aplicarEdicao(anterior, pendente, estiloTextoRef.current));
	}, []);

	// Trocar de ferramenta ou virar a pagina confirma o que estava sendo digitado, em vez de descartar.
	useEffect(
		() => {
			confirmarEdicao();
			setSobreTexto(false);
			setSelecionado(null);
		},
		[ ferramenta, chave, confirmarEdicao ]
	);

	// Esc larga o texto escolhido. So existe enquanto ha selecao, entao a tecla nao e
	// vigiada o tempo todo: as telas de material deixaram de observar o teclado de proposito.
	useEffect(
		() => {
			if (!selecionado) return;

			const largar = (evento: KeyboardEvent) => {
				if (evento.key === 'Escape') setSelecionado(null);
			};
			window.addEventListener('keydown', largar);

			return () => window.removeEventListener('keydown', largar);
		},
		[ selecionado ]
	);

	const abrirEdicao = (nova: Omit<Edicao, 'id' | 'chave'>) => {
		proximaEdicaoRef.current += 1;
		const aberta: Edicao = { ...nova, id: proximaEdicaoRef.current, chave };

		edicaoRef.current = aberta;
		setEdicao(aberta);
	};

	const digitar = (conteudo: string) => {
		if (!edicaoRef.current) return;

		const atualizada = { ...edicaoRef.current, conteudo };
		edicaoRef.current = atualizada;
		setEdicao(atualizada);
	};

	const cancelarEdicao = () => {
		edicaoRef.current = null;
		setEdicao(null);
	};

	/** O ultimo texto desenhado vence, que e o que esta por cima na pagina. */
	const textoEm = (ponto: Ponto) => {
		const ctx = canvasBaseRef.current?.getContext('2d') || null;

		return marcas
			.slice()
			.reverse()
			.find((marca): marca is Texto => marca.ferramenta === 'texto' && textoAtingido(ctx, marca, ponto, tela));
	};

	const escreverEm = (ponto: Ponto) => {
		// confirma antes de abrir para que o campo anterior nao caia sobre a nova edicao
		confirmarEdicao();
		setSelecionado(null);
		setTamanhoTexto(tamanhoNovoTextoRef.current);
		abrirEdicao({ posicao: ponto, conteudo: '' });
	};

	/** Troca o texto por uma versao alterada, mantendo a ordem de desenho e a selecao em cima dele. */
	const substituirTexto = (anterior: Texto, atual: Texto) => {
		setSelecionado(atual);
		setAnotacoes((anotacoesAnteriores) => {
			const daPagina = anotacoesAnteriores[chave] || VAZIO;

			return { ...anotacoesAnteriores, [chave]: daPagina.map((marca) => (marca === anterior ? atual : marca)) };
		});
	};

	// Reabrir traz cor e tamanho do texto para a paleta, tanto para mostrar como ele esta
	// quanto para que confirmar sem mexer em nada nao o reescreva com outro estilo.
	const editarTexto = (existente: Texto) => {
		confirmarEdicao();
		setSelecionado(null);
		setCor(existente.cor);
		setTamanhoTexto(existente.tamanho);
		setFundo(existente.fundo);
		abrirEdicao({ posicao: existente.posicao, conteudo: existente.conteudo, substituindo: existente });
	};

	const selecionarTexto = (existente: Texto) => {
		confirmarEdicao();
		setSelecionado(existente);
		// a paleta passa a valer para ele, entao precisa mostrar o estilo que ele ja tem
		setCor(existente.cor);
		setTamanhoTexto(existente.tamanho);
		setFundo(existente.fundo);
	};

	/** Troca o texto arrastado pela copia no lugar novo, mantendo a ordem de desenho. */
	const moverTexto = (ponto: Ponto) => {
		const movimento = movimentoRef.current;
		if (!movimento) return;

		const anterior = movimento.marca;
		const dentroDaPagina = (valor: number) => Math.min(1, Math.max(0, valor));
		const atual: Texto = {
			...anterior,
			// o canto e preso a pagina para que um texto solto la fora nao fique impossivel de pegar de volta
			posicao: {
				x: dentroDaPagina(ponto.x - movimento.deslocamento.x),
				y: dentroDaPagina(ponto.y - movimento.deslocamento.y)
			}
		};

		movimento.marca = atual;
		substituirTexto(anterior, atual);
	};

	/**
	 * A alca fica no canto oposto ao da origem, entao a largura arrastada dividida pela
	 * largura de quando o arrasto comecou da direto o fator do novo tamanho.
	 */
	const redimensionarTexto = (ponto: Ponto) => {
		const redimensionamento = redimensionamentoRef.current;
		if (!redimensionamento || !redimensionamento.larguraInicial) return;

		const larguraArrastada = (ponto.x - redimensionamento.origem.x) * tela.largura;
		const fator = larguraArrastada / redimensionamento.larguraInicial;
		const tamanho = tamanhoValido(redimensionamento.tamanhoInicial * fator);

		const anterior = redimensionamento.marca;
		if (tamanho === anterior.tamanho) return;

		const atual: Texto = { ...anterior, tamanho };
		redimensionamento.marca = atual;
		setTamanhoTexto(tamanho);
		substituirTexto(anterior, atual);
	};

	/** A alca e um botao de verdade, entao as setas precisam redimensionar como o arrasto. */
	const redimensionarPeloTeclado = (tecla: string) => {
		const passos: Record<string, number> = {
			ArrowRight: PASSO_ALCA,
			ArrowUp: PASSO_ALCA,
			ArrowLeft: 1 / PASSO_ALCA,
			ArrowDown: 1 / PASSO_ALCA
		};
		const passo = passos[tecla];
		if (!passo || !selecionado) return false;

		const tamanho = tamanhoValido(selecionado.tamanho * passo);
		if (tamanho !== selecionado.tamanho) {
			setTamanhoTexto(tamanho);
			substituirTexto(selecionado, { ...selecionado, tamanho });
		}

		return true;
	};

	const passouDoLimiar = (inicio: Ponto, ponto: Ponto) =>
		Math.hypot((ponto.x - inicio.x) * tela.largura, (ponto.y - inicio.y) * tela.altura) > LIMIAR_ARRASTO;

	// O pointerdown abre e foca o campo de texto, mas o mousedown que vem logo depois
	// ainda tem sua acao padrao: o canvas nao e focavel, entao o navegador manda o foco
	// para o body, o blur confirma um texto vazio e o campo some antes da primeira tecla.
	// Cancelar aqui tambem evita a selecao arrastada por cima da pagina ao desenhar.
	const handleMouseDown = (evento: React.MouseEvent<HTMLCanvasElement>) => {
		evento.preventDefault();
	};

	// O clique escolhe o texto e o duplo clique abre para escrever, como em qualquer
	// caixa de texto de apresentacao: o pointerup ja selecionou, aqui so troca para a edicao.
	const handleDoubleClick = (evento: React.MouseEvent<HTMLCanvasElement>) => {
		if (ferramenta !== 'texto' || !tela.largura) return;

		const ponto = pontoNaPagina(evento.clientX, evento.clientY);
		const existente = ponto && textoEm(ponto);

		if (existente) editarTexto(existente);
	};

	// Pelo mesmo motivo do canvas: escolher cor ou tamanho com o campo aberto nao pode
	// tirar o foco dele, senao o blur confirma o texto com o estilo que se acabou de trocar.
	const manterFoco = (evento: React.MouseEvent) => evento.preventDefault();

	const handlePointerDown = (evento: React.PointerEvent<HTMLCanvasElement>) => {
		if (ferramenta === 'cursor' || !tela.largura) return;

		const ponto = posicaoNormalizada(evento);

		if (ferramenta === 'texto') {
			const existente = textoEm(ponto);

			if (!existente) {
				escreverEm(ponto);

				return;
			}

			// Agarrar um texto ainda nao decide nada: quem resolve e o pointerup, conforme
			// o ponteiro tenha andado (mover) ou nao (selecionar).
			evento.currentTarget.setPointerCapture(evento.pointerId);
			confirmarEdicao();
			movimentoRef.current = {
				marca: existente,
				deslocamento: { x: ponto.x - existente.posicao.x, y: ponto.y - existente.posicao.y },
				inicio: ponto,
				moveu: false
			};

			return;
		}

		evento.currentTarget.setPointerCapture(evento.pointerId);

		if (ferramenta === 'borracha') {
			apagandoRef.current = true;
			apagarEm(ponto);
			return;
		}

		tracoAtualRef.current = {
			ferramenta,
			cor,
			espessura: ferramenta === 'marcador' ? espessura * MULTIPLICADOR_MARCADOR : espessura,
			pontos: [ ponto ]
		};
		desenharTracoAtual();
	};

	const handlePointerMove = (evento: React.PointerEvent<HTMLCanvasElement>) => {
		const movimento = movimentoRef.current;
		if (movimento) {
			const ponto = posicaoNormalizada(evento);
			if (!movimento.moveu && !passouDoLimiar(movimento.inicio, ponto)) return;

			movimento.moveu = true;
			moverTexto(ponto);

			return;
		}

		if (apagandoRef.current) {
			apagarEm(posicaoNormalizada(evento));
			return;
		}

		const traco = tracoAtualRef.current;
		if (!traco) {
			// o cursor de mover e o unico aviso de que o texto sob o ponteiro pode ser arrastado
			if (ferramenta === 'texto') setSobreTexto(!!textoEm(posicaoNormalizada(evento)));

			return;
		}

		traco.pontos.push(posicaoNormalizada(evento));
		desenharTracoAtual();
	};

	const handlePointerUp = (evento: React.PointerEvent<HTMLCanvasElement>) => {
		if (evento.currentTarget.hasPointerCapture(evento.pointerId)) {
			evento.currentTarget.releasePointerCapture(evento.pointerId);
		}
		apagandoRef.current = false;

		const movimento = movimentoRef.current;
		if (movimento) {
			movimentoRef.current = null;
			// texto que nao andou era um clique: seleciona, e o duplo clique e que abre para corrigir
			if (!movimento.moveu) selecionarTexto(movimento.marca);

			return;
		}

		const traco = tracoAtualRef.current;
		if (!traco) return;

		tracoAtualRef.current = null;
		desenharTracoAtual();
		setAnotacoes((anterior) => ({ ...anterior, [chave]: (anterior[chave] || VAZIO).concat(traco) }));
	};

	// Com um texto escolhido a paleta age sobre ele. Sem isso a selecao seria um beco sem
	// saida: mostraria o estilo do texto e nao deixaria troca-lo sem reabrir o campo.
	const escolherCor = (valor: string) => {
		setCor(valor);
		if (selecionado) substituirTexto(selecionado, { ...selecionado, cor: valor });
	};

	const escolherFundo = (valor?: string) => {
		setFundo(valor);
		if (selecionado) substituirTexto(selecionado, { ...selecionado, fundo: valor });
	};

	const escolherTamanhoTexto = (valor: number) => {
		setTamanhoTexto(valor);
		tamanhoNovoTextoRef.current = valor;
		if (selecionado) substituirTexto(selecionado, { ...selecionado, tamanho: valor });
	};

	// Desfazer e limpar descartam o que estava sendo digitado: confirmar traria de volta
	// justamente o texto que o usuario acabou de mandar apagar.
	const desfazer = () => {
		cancelarEdicao();
		setSelecionado(null);
		setAnotacoes((anterior) => {
			const atuais = anterior[chave] || VAZIO;
			if (atuais.length === 0) return anterior;

			return { ...anterior, [chave]: atuais.slice(0, -1) };
		});
	};

	const limparPagina = () => {
		cancelarEdicao();
		setSelecionado(null);
		setAnotacoes((anterior) => (anterior[chave] ? { ...anterior, [chave]: VAZIO } : anterior));
	};

	// A moldura sai enquanto o campo esta aberto: quem edita ja tem o proprio contorno.
	const caixaSelecao = (() => {
		const ctx = canvasBaseRef.current?.getContext('2d');
		if (!selecionado || edicao || !ctx || !tela.largura) return null;

		const { largura, altura } = medirTexto(ctx, selecionado, tela);
		// com tarja a moldura abraca o fundo, senao ela cortaria a faixa pelas laterais
		const folga = selecionado.fundo ? folgaFundo(fonteEmPx(selecionado.tamanho, tela)) : 0;

		return {
			esquerda: selecionado.posicao.x * tela.largura - folga,
			topo: selecionado.posicao.y * tela.altura,
			largura: largura + folga * 2,
			altura
		};
	})();

	const iniciarRedimensionamento = (evento: React.PointerEvent<HTMLDivElement>) => {
		const ctx = canvasBaseRef.current?.getContext('2d');
		if (!selecionado || !ctx) return;

		evento.currentTarget.setPointerCapture(evento.pointerId);
		redimensionamentoRef.current = {
			marca: selecionado,
			origem: selecionado.posicao,
			larguraInicial: medirTexto(ctx, selecionado, tela).largura,
			tamanhoInicial: selecionado.tamanho
		};
	};

	const desenhando = ferramenta === 'caneta' || ferramenta === 'marcador';
	const escolhendoCor = desenhando || ferramenta === 'texto';

	return (
		<Flex
			className="wrap"
			position="relative"
			width="100vw"
			height="100vh"
			paddingBottom="60px"
			paddingRight="64px"
			background="blue.900"
			overflow="hidden"
			onContextMenu={(evento) => evento.preventDefault()}
		>
			{/* a area util exclui a barra inferior da pagina e a canaleta da paleta, para nada cobrir o material */}
			<Flex ref={medirArea} flex="1" minWidth={0} alignItems="center" justifyContent="center">
				{file && (
					<Box ref={medirPagina} position="relative" lineHeight="0" boxShadow="dark-lg">
						<Document file={file} onLoadSuccess={onDocumentLoadSuccess}>
							<Page
								pageNumber={pageNumber}
								width={larguraPagina || undefined}
								renderTextLayer={false}
								renderAnnotationLayer={false}
								onLoadSuccess={({ originalWidth, originalHeight }) => {
									setProporcao(originalWidth / originalHeight);
								}}
							/>
						</Document>
						<canvas
							ref={canvasBaseRef}
							style={{
								position: 'absolute',
								left: 0,
								top: 0,
								width: '100%',
								height: '100%',
								pointerEvents: 'none'
							}}
						/>
						<canvas
							ref={canvasAtualRef}
							data-testid="quadro-interativo"
							style={{
								position: 'absolute',
								left: 0,
								top: 0,
								width: '100%',
								height: '100%',
								touchAction: 'none',
								pointerEvents: ferramenta === 'cursor' ? 'none' : 'auto',
								cursor: ferramenta === 'texto' && sobreTexto ? 'move' : cursorPorFerramenta[ferramenta]
							}}
							onMouseDown={handleMouseDown}
							onPointerDown={handlePointerDown}
							onPointerMove={handlePointerMove}
							onPointerUp={handlePointerUp}
							onPointerCancel={handlePointerUp}
							onPointerLeave={() => setSobreTexto(false)}
							onDoubleClick={handleDoubleClick}
						/>
						{caixaSelecao && (
							<Box
								data-testid="selecao-texto"
								position="absolute"
								left={`${caixaSelecao.esquerda}px`}
								top={`${caixaSelecao.topo}px`}
								width={`${caixaSelecao.largura}px`}
								height={`${caixaSelecao.altura}px`}
								zIndex={2}
								borderWidth="1.5px"
								borderStyle="solid"
								borderColor="blue.500"
								borderRadius="2px"
								// a moldura nao rouba o ponteiro: arrastar o texto continua sendo com o canvas
								pointerEvents="none"
							>
								<Box
									as="button"
									type="button"
									data-testid="alca-redimensionar"
									aria-label="Redimensionar texto"
									position="absolute"
									right={`${-LADO_ALCA / 2}px`}
									bottom={`${-LADO_ALCA / 2}px`}
									width={`${LADO_ALCA}px`}
									height={`${LADO_ALCA}px`}
									borderRadius="2px"
									background="white"
									borderWidth="2px"
									borderStyle="solid"
									borderColor="blue.500"
									boxShadow="0 1px 3px rgba(14, 31, 56, 0.35)"
									cursor="nwse-resize"
									pointerEvents="auto"
									sx={{ touchAction: 'none' }}
									onMouseDown={manterFoco}
									onPointerDown={iniciarRedimensionamento}
									onPointerMove={(evento: React.PointerEvent<HTMLDivElement>) => {
										if (!redimensionamentoRef.current) return;

										const ponto = pontoNaPagina(evento.clientX, evento.clientY);
										if (ponto) redimensionarTexto(ponto);
									}}
									onPointerUp={() => {
										redimensionamentoRef.current = null;
									}}
									onPointerCancel={() => {
										redimensionamentoRef.current = null;
									}}
									onKeyDown={(evento: React.KeyboardEvent<HTMLDivElement>) => {
										if (redimensionarPeloTeclado(evento.key)) evento.preventDefault();
									}}
								/>
							</Box>
						)}
						{edicao && (
							<textarea
								key={edicao.id}
								ref={prepararCampo}
								data-testid="campo-texto"
								// sem isto o campo nasce com as duas linhas padrao do textarea e
								// `ajustarAltura` nunca consegue encolher para menos que isso
								rows={1}
								aria-label="Texto sobre o material"
								value={edicao.conteudo}
								onChange={(evento) => digitar(evento.target.value)}
								onBlur={() => {
									// so vale para a edicao que este campo abriu: clicar em outro ponto ja confirmou a anterior
									if (edicaoRef.current?.id === edicao.id) confirmarEdicao();
								}}
								onKeyDown={(evento) => {
									if (evento.key === 'Escape') cancelarEdicao();
									if (evento.key === 'Enter' && (evento.ctrlKey || evento.metaKey)) confirmarEdicao();
								}}
								style={{
									position: 'absolute',
									left: `${edicao.posicao.x * 100}%`,
									top: `${edicao.posicao.y * 100}%`,
									width: larguraCampo ? `${larguraCampo}px` : undefined,
									zIndex: 2,
									// o reset da Chakra deixa tudo em border-box, e ai o padding comeria
									// a largura ja medida para o que foi digitado
									boxSizing: 'content-box',
									margin: `0 0 0 ${-folgaCampo}px`,
									padding: `0 ${folgaCampo}px`,
									border: 'none',
									// contorno em vez de borda para o texto nao andar ao ser confirmado
									outline: '1px dashed rgba(14, 31, 56, 0.6)',
									borderRadius: '2px',
									resize: 'none',
									overflow: 'hidden',
									// sem tarja o campo ainda ganha um branco por baixo, senao nao da para
									// ler o que se digita por cima do material
									background: fundo || 'rgba(255, 255, 255, 0.92)',
									color: cor,
									// fonte em propriedades separadas: o atalho `font` junto de lineHeight
									// e trocado a cada mudanca de tamanho, e o React avisa do conflito
									fontFamily: FONTE_TEXTO,
									fontSize: `${fonteEmPx(tamanhoTexto, tela)}px`,
									lineHeight: ALTURA_LINHA
								}}
							/>
						)}
					</Box>
				)}
			</Flex>

			<Flex
				position="absolute"
				right="16px"
				top="50%"
				transform="translateY(-50%)"
				flexDir="column"
				alignItems="center"
				gap="6px"
				padding="6px"
				borderRadius="14px"
				background="whiteAlpha.900"
				backdropFilter="blur(6px)"
				boxShadow="dark-lg"
				zIndex={900}
				visibility={file ? 'visible' : 'hidden'}
			>
				{FERRAMENTAS.map(({ id, rotulo, dica, Icone }) => (
					<Tooltip key={id} label={dica || rotulo} placement="left">
						<IconButton
							aria-label={rotulo}
							aria-pressed={ferramenta === id}
							icon={<Icone />}
							size="sm"
							variant={ferramenta === id ? 'solid' : 'ghost'}
							colorScheme="blue"
							onClick={() => setFerramenta(id)}
						/>
					</Tooltip>
				))}

				{escolhendoCor && (
					<Flex flexDir="column" alignItems="center" gap="6px" borderTopWidth="1px" borderColor="mist" paddingTop="6px">
						{CORES.map((opcao) => (
							<Tooltip key={opcao.valor} label={opcao.nome} placement="left">
								<Box
									as="button"
									type="button"
									aria-label={opcao.nome}
									aria-pressed={cor === opcao.valor}
									width="20px"
									height="20px"
									borderRadius="full"
									background={opcao.valor}
									borderWidth="2px"
									borderColor={cor === opcao.valor ? 'ink' : 'transparent'}
									onMouseDown={manterFoco}
									onClick={() => escolherCor(opcao.valor)}
								/>
							</Tooltip>
						))}
						{desenhando &&
							ESPESSURAS.map((opcao, indice) => (
								<Box
									key={opcao}
									as="button"
									type="button"
									aria-label={`Espessura ${indice + 1}`}
									aria-pressed={espessura === opcao}
									display="flex"
									alignItems="center"
									justifyContent="center"
									width="20px"
									height="20px"
									borderRadius="full"
									background={espessura === opcao ? 'blue.50' : 'transparent'}
									onClick={() => setEspessura(opcao)}
								>
									<Box width={`${6 + indice * 4}px`} height={`${2 + indice * 2}px`} borderRadius="full" background="ink" />
								</Box>
							))}
						{ferramenta === 'texto' &&
							TAMANHOS_TEXTO.map((opcao, indice) => (
								<Box
									key={opcao}
									as="button"
									type="button"
									aria-label={`Tamanho ${indice + 1}`}
									aria-pressed={tamanhoTexto === opcao}
									display="flex"
									alignItems="center"
									justifyContent="center"
									width="20px"
									height="20px"
									borderRadius="full"
									lineHeight="1"
									fontWeight="semibold"
									color="ink"
									fontSize={`${9 + indice * 3}px`}
									background={tamanhoTexto === opcao ? 'blue.50' : 'transparent'}
									onMouseDown={manterFoco}
									onClick={() => escolherTamanhoTexto(opcao)}
								>
									A
								</Box>
							))}
						{ferramenta === 'texto' &&
							FUNDOS.map((opcao) => (
								<Tooltip key={opcao.nome} label={opcao.nome} placement="left">
									<Box
										as="button"
										type="button"
										aria-label={opcao.nome}
										aria-pressed={fundo === opcao.valor}
										width="20px"
										height="20px"
										// quadrado, e nao redondo como a cor da letra, para as duas fileiras
										// nao passarem por uma paleta so
										borderRadius="4px"
										background={opcao.valor || RISCO_SEM_FUNDO}
										borderWidth="2px"
										borderColor={fundo === opcao.valor ? 'ink' : 'mist'}
										onMouseDown={manterFoco}
										onClick={() => escolherFundo(opcao.valor)}
									/>
								</Tooltip>
							))}
					</Flex>
				)}

				<Flex flexDir="column" alignItems="center" gap="6px" borderTopWidth="1px" borderColor="mist" paddingTop="6px">
					<Tooltip label="Desfazer" placement="left">
						<IconButton
							aria-label="Desfazer"
							icon={<MdUndo />}
							size="sm"
							variant="ghost"
							colorScheme="blue"
							isDisabled={marcas.length === 0}
							onClick={desfazer}
						/>
					</Tooltip>
					<Tooltip label="Limpar página" placement="left">
						<IconButton
							aria-label="Limpar página"
							icon={<MdDeleteOutline />}
							size="sm"
							variant="ghost"
							colorScheme="red"
							isDisabled={marcas.length === 0}
							onClick={limparPagina}
						/>
					</Tooltip>
				</Flex>
			</Flex>
		</Flex>
	);
}
